import type { AppLocale } from "@/config/app-locale";

export const authCopy = {
  en: {
    brand: {
      headingLines: ["Ready to build", "your Canadian", "resume?"],
      description:
        "Create a standout resume tailored for Canadian employers in minutes.",
    },
    signIn: {
      title: "Sign In",
      socialProviders: {
        google: "Continue with Google",
      },
      divider: "or",
      emailLabel: "Email address",
      emailRequired: "Email is required.",
      emailInvalid: "Enter a valid email address.",
      submit: "Send me a sign-in link",
      submitting: "Sending sign-in link…",
      googleRedirecting: "Redirecting to Google…",
      microcopy:
        "No password needed. We’ll email you a secure link to log in instantly.",
      errors: {
        google: "Google sign-in could not be completed. Please try again.",
        rateLimited: "Too many requests. Please wait before trying again.",
        unavailable: "We couldn’t send a sign-in link. Please try again.",
      },
      confirmation: {
        title: "Check your inbox! 📧",
        sentTo: "We sent a secure login link to:",
        firstStep: "Click the link in the email to access your workspace.",
        secondStep: "Can’t find it? Check your spam or junk folder.",
        resend: "Resend email",
        resendIn: (seconds: number) => `Resend in ${seconds}s`,
        resent: "A new secure sign-in link has been sent.",
        changeEmail: "Change email address",
      },
      legalPrefix: "By continuing, you agree to our",
      terms: "Terms of Service",
      privacy: "Privacy Policy",
    },
    callback: {
      verifying: "Verifying your secure sign-in link…",
      success: "Sign-in successful. Redirecting…",
      invalid: "This sign-in link is invalid or has expired.",
      failure: "We couldn’t complete sign-in. Please request a new link.",
      returnToLogin: "Return to sign in",
    },
    language: { en: "EN", fr: "FR" },
  },
  fr: {
    brand: {
      headingLines: ["Prêt à créer", "votre CV", "canadien?"],
      description:
        "Créez un CV remarquable, adapté aux employeurs canadiens, en quelques minutes.",
    },
    signIn: {
      title: "Connexion",
      socialProviders: {
        google: "Continuer avec Google",
      },
      divider: "ou",
      emailLabel: "Adresse courriel",
      emailRequired: "Le courriel est requis.",
      emailInvalid: "Entrez une adresse courriel valide.",
      submit: "Envoyez-moi un lien de connexion",
      submitting: "Envoi du lien de connexion…",
      googleRedirecting: "Redirection vers Google…",
      microcopy:
        "Aucun mot de passe requis. Nous vous enverrons un lien sécurisé pour vous connecter instantanément.",
      errors: {
        google: "La connexion avec Google n’a pas pu être complétée. Veuillez réessayer.",
        rateLimited: "Trop de tentatives. Veuillez patienter avant de réessayer.",
        unavailable: "Nous n’avons pas pu envoyer de lien de connexion. Veuillez réessayer.",
      },
      confirmation: {
        title: "Vérifiez votre boîte de réception! 📧",
        sentTo: "Nous avons envoyé un lien de connexion sécurisé à :",
        firstStep: "Cliquez sur le lien dans le courriel pour accéder à votre espace de travail.",
        secondStep: "Vous ne le trouvez pas? Vérifiez votre dossier de courrier indésirable.",
        resend: "Renvoyer le courriel",
        resendIn: (seconds: number) => `Renvoyer dans ${seconds}s`,
        resent: "Un nouveau lien de connexion sécurisé a été envoyé.",
        changeEmail: "Changer l’adresse courriel",
      },
      legalPrefix: "En continuant, vous acceptez nos",
      terms: "Conditions d’utilisation",
      privacy: "Politique de confidentialité",
    },
    callback: {
      verifying: "Vérification de votre lien de connexion sécurisé…",
      success: "Connexion réussie. Redirection…",
      invalid: "Ce lien de connexion est invalide ou a expiré.",
      failure: "Nous n’avons pas pu terminer la connexion. Veuillez demander un nouveau lien.",
      returnToLogin: "Retour à la connexion",
    },
    language: { en: "EN", fr: "FR" },
  },
} as const satisfies Record<AppLocale, object>;
